/**
 * いま動いている補正サーバー・ゲームサーバー（Issue #882。撮影の帯 ShootNav の下に出す）。
 *
 * どの台（EC2・家・会場の PC）のサーバーも synchro-client から立ち、数秒ごとに本番へ名乗る（`LiveServerHeartbeat`）。
 * 本番は名乗りの新しいもののうち、いちばん後から来たものを使う（前のものは控え。使っているものが途絶えたら控えに戻る）。
 * ここは本番の一覧（`GetLiveServers`）を 5 秒ごとに読んで、種類ごとに「使っている／控え／途絶えた」を出すだけ（EC2 の起動・停止はダッシュボード）。
 */
import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/api';
import { parseServerTime } from '../utils/serverTime';

const KINDS = [
  { kind: 'correct', label: '補正サーバー', none: '動いていません（EC2 の補正サーバーの名前 live.ds… を使います）' },
  { kind: 'game', label: 'ゲームサーバー', none: '動いていません（ゲーム映像は描かれません）' },
];
const PLACES = { ec2: 'EC2', local: 'PC' };

/** 1 台の状態 → [文言, 色] */
export function serverBadge(s) {
  if (s.active) return ['使っている', '#9be7a1'];
  if (s.alive) return ['控え', '#f0c36b'];
  return [`途絶えた（${Math.round(s.age_sec)} 秒前）`, '#8a93a8'];
}

/** 来た時刻 → 「10/15 21:03 から」 */
export function sinceText(iso) {
  if (!iso) return '';
  const t = parseServerTime(iso);
  if (!t) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${t.getMonth() + 1}/${t.getDate()} ${p(t.getHours())}:${p(t.getMinutes())} から`;
}

export default function LiveServersStatus() {
  const [servers, setServers] = useState(null);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (typeof api.project !== 'function') return;
    try {
      const r = await api.project('GetLiveServers', {});
      setServers(Array.isArray(r?.servers) ? r.servers : []);
      setError(null);
    } catch (e) {
      setError('サーバーの一覧を読めません');
    }
  }, []);

  useEffect(() => {
    let alive = true;
    let timer;
    const loop = async () => {
      await refresh();
      if (alive) timer = setTimeout(loop, 5000);
    };
    loop();
    return () => { alive = false; clearTimeout(timer); };
  }, [refresh]);

  return (
    <div data-testid="live-servers" style={{ fontSize: 12, padding: '6px 10px', border: '1px solid #223', borderRadius: 6, margin: '8px 0 12px' }}>
      {KINDS.map(({ kind, label, none }) => {
        const list = (servers || []).filter((s) => s.kind === kind);
        return (
          <div key={kind} data-testid={`live-servers-${kind}`} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'baseline', margin: '2px 0' }}>
            <strong style={{ minWidth: 96 }}>{label}</strong>
            {servers === null ? <span style={{ color: '#8a93a8' }}>確認中…</span> : null}
            {servers !== null && !list.some((s) => s.alive) ? <span style={{ color: '#8a93a8' }}>{none}</span> : null}
            {list.map((s) => {
              const [text, color] = serverBadge(s);
              return (
                <span key={s.server_id} data-testid="live-server-row" title={s.server_id} style={{ color: '#b8c0d4' }}>
                  <span style={{ color, fontWeight: 700 }}>● {text}</span>
                  {' '}{s.name || s.server_id}（{PLACES[s.place] ?? s.place}）
                  {s.url ? ` ${s.url}` : ''}{s.browser_url ? ` / ${s.browser_url}` : ''}
                  {' '}<span style={{ color: '#8a93a8' }}>{sinceText(s.started_at)}</span>
                </span>
              );
            })}
          </div>
        );
      })}
      {error ? <div style={{ color: '#ff8a8a' }}>{error}</div> : null}
    </div>
  );
}
